import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import Button from './Button';
import { SHELL_ESCRITORIO_ENABLED } from '../../lib/shellEscritorio';

/**
 * Toast: feedback efímero post-acción (PAE guardado, copia exitosa, fallo
 * recoverable). Un solo toast visible a la vez; los siguientes hacen cola.
 * Estilo editorial del addendum: rules horizontales en lugar de fondo
 * saturado.
 *
 * Uso:
 *   <ToastProvider>
 *     <App />
 *     <ToastViewport />
 *   </ToastProvider>
 *
 *   const { toast } = useToast();
 *   toast.success('PAE guardado en tu cuenta.');
 *   toast.error('No pudimos guardar tu PAE.');
 *   toast.info('Tu sesión expirará en 5 minutos.', { duration: 0 });
 */

const ToastContext = createContext(null);

const DEFAULT_DURATION = 3500;

/**
 * Provee el método `toast` (con .success/.error/.info) a la app.
 */
export function ToastProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const idRef = useRef(0);

  const enqueue = useCallback((tone, message, options = {}) => {
    idRef.current += 1;
    const id = idRef.current;
    const item = {
      id,
      tone,
      message,
      duration: options.duration ?? DEFAULT_DURATION,
      dismissible: options.dismissible !== false,
      action: options.action,
    };
    setQueue((prev) => [...prev, item]);
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setQueue((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useMemo(
    () => ({
      success: (msg, opts) => enqueue('success', msg, opts),
      error:   (msg, opts) => enqueue('error',   msg, opts),
      info:    (msg, opts) => enqueue('info',    msg, opts),
    }),
    [enqueue]
  );

  const value = useMemo(() => ({ toast, queue, dismiss }), [toast, queue, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/**
 * Hook para invocar toasts imperativamente. Devuelve { toast }.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback no-op para que el árbol no crashee si el provider falta.
    const noop = () => 0;
    return { toast: { success: noop, error: noop, info: noop } };
  }
  return { toast: ctx.toast };
}

/**
 * Renderiza el toast actualmente activo (si lo hay). Montar una sola vez en
 * el root del árbol, dentro del <ToastProvider>.
 */
export function ToastViewport() {
  const ctx = useContext(ToastContext);
  if (!ctx) return null;
  const current = ctx.queue[0];
  if (!current) return null;

  return (
    // Sobre la barra de pestañas (nav v2: 3.5rem + safe area): antes el toast
    // caía en la franja de la tab bar y tapaba la navegación justo tras cada
    // acción (auditoría 2026-07-27). En pantallas sin tab bar queda un poco
    // más alto, que es inocuo; taparla nunca lo era.
    // En lg con el shell de escritorio la tab bar no existe (vive en el header),
    // así que el toast baja los 3.5rem que le reservaba.
    <div
      className={`fixed inset-x-4 z-50 flex justify-center pointer-events-none bottom-[calc(3.5rem_+_env(safe-area-inset-bottom,0px)_+_0.75rem)]${SHELL_ESCRITORIO_ENABLED ? " lg:bottom-[calc(env(safe-area-inset-bottom,0px)_+_0.75rem)]" : ""}`}
    >
      <div className="pointer-events-auto w-full max-w-md">
        <Toast key={current.id} item={current} onDismiss={() => ctx.dismiss(current.id)} />
      </div>
    </div>
  );
}

function Toast({ item, onDismiss }) {
  const { tone, message, duration, dismissible, action } = item;
  const cfg = TONE_CONFIG[tone] || TONE_CONFIG.info;
  const IconComponent = cfg.Icon;
  const role = tone === 'error' ? 'alert' : 'status';

  useEffect(() => {
    if (!duration) return undefined;
    const t = setTimeout(onDismiss, duration);
    return () => clearTimeout(t);
  }, [duration, onDismiss]);

  return (
    <div
      role={role}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className="flex items-center gap-3 border-t border-b border-edge bg-paper py-3 px-4 shadow-tk-sm"
    >
      <span className={`${cfg.colorClass} flex-shrink-0`} aria-hidden="true">
        <IconComponent size={14} strokeWidth={1.6} />
      </span>
      <span className="flex-1 min-w-0 text-small text-ink font-sans">
        {message}
      </span>
      {action && (
        <Button variant="ghost" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar"
          // p-5 -m-4 extiende el área táctil a 50px (icon 14px + 2×18px)
          // pero el margen negativo compensa casi todo el padding extra, así
          // que el footprint en el layout queda igual al p-1 original (el
          // toast no crece visualmente, solo el hit area invisible).
          className="flex-shrink-0 text-ink-muted hover:text-ink transition-colors p-5 -m-4"
        >
          <X size={14} strokeWidth={1.6} />
        </button>
      )}
    </div>
  );
}

const TONE_CONFIG = {
  success: { Icon: CheckCircle2, colorClass: 'text-system-success' },
  error:   { Icon: AlertCircle,  colorClass: 'text-system-error' },
  info:    { Icon: Info,         colorClass: 'text-system-info' },
};

// Default export: el provider, que es la API "principal" del módulo.
export default ToastProvider;
